import { Alert, AlertIcon, Skeleton } from '@chakra-ui/react';
import { useMemo } from 'react';

import type { CompanyMetrics } from '../hooks/useCompetitorComposition';
import type { Competitor } from '../hooks/useCompetitors';
import { useCurrencyFormatter } from '../hooks/useCurrencyFormatter';
import type { EmployerHealth } from '../hooks/useEmployerHealth';
import type { HiddenGem } from '../hooks/useHiddenGems';
import type { RoleLevel } from '../hooks/useResolveProfileIds';
import type { CompanyLeverageSignal } from '../hooks/useRolePostingSignals';
import type { CompanySentiment } from '../hooks/useSentimentComparison';
import type { WorkHistoryCompany } from '../hooks/useWorkHistoryCompanies';
import { SectionCard } from '../SectionCard';
import { CompetitorTable } from './CompetitorTable';
import { DemandLeverageSignals } from './DemandLeverageSignals';
import { HiddenGemsCard } from './HiddenGemsCard';

interface BetterEmployersSectionProps {
  metrics: CompanyMetrics[];
  sentiments: CompanySentiment[];
  healthData: EmployerHealth[];
  competitors: Competitor[];
  currentCompanyId: number | null;
  hiddenGems: HiddenGem[];
  leverageSignals: CompanyLeverageSignal[];
  leverageRoleName: string | null;
  signalsLoading: boolean;
  signalsError: string | null;
  workHistoryCompanies: WorkHistoryCompany[];
  isLoading: boolean;
  isResolving: boolean;
  userSalary: number;
  userRoleLevel: RoleLevel | null;
  isIndustryFallback: boolean;
  /** Which company is currently selected for competitor lookup */
  selectedCompanyRcid: number | null;
  onCompanySelect: (rcid: number | null) => void;
}

export const BetterEmployersSection = ({
  metrics,
  sentiments,
  healthData,
  competitors,
  currentCompanyId,
  hiddenGems,
  leverageSignals,
  leverageRoleName,
  signalsLoading,
  signalsError,
  workHistoryCompanies,
  isLoading,
  isResolving,
  userSalary,
  userRoleLevel,
  isIndustryFallback,
  selectedCompanyRcid,
  onCompanySelect,
}: BetterEmployersSectionProps) => {
  const salaryFormatter = useCurrencyFormatter();

  const competitorSourceRcid = selectedCompanyRcid ?? currentCompanyId;

  // When a previous company is selected, highlight it as "(Former)" in the table
  const highlightedCompanyIds = useMemo(() => {
    const s = new Set<number>();
    if (selectedCompanyRcid) s.add(selectedCompanyRcid);
    return s;
  }, [selectedCompanyRcid]);

  // Filter data to only include: current company + selected previous company + competitors.
  // The full metrics/sentiments/healthData arrays also contain other historical companies
  // needed by Track A (Employer Outlook), so we filter here for the competitor table.
  const allowedIds = useMemo(() => {
    const ids = new Set(competitors.map((c) => c.id));
    if (currentCompanyId) ids.add(currentCompanyId);
    if (selectedCompanyRcid) ids.add(selectedCompanyRcid);
    return ids;
  }, [competitors, currentCompanyId, selectedCompanyRcid]);

  const tableMetrics = useMemo(
    () => metrics.filter((m) => allowedIds.has(m.companyId)),
    [metrics, allowedIds]
  );

  const tableSentiments = useMemo(
    () => sentiments.filter((s) => allowedIds.has(s.companyId)),
    [sentiments, allowedIds]
  );

  const tableHealthData = useMemo(
    () => healthData.filter((h) => allowedIds.has(h.companyId)),
    [healthData, allowedIds]
  );

  if (isResolving) {
    return (
      <SectionCard heading="Find a New Company" spacing={4}>
        <Skeleton height="200px" borderRadius="md" />
      </SectionCard>
    );
  }

  if (tableMetrics.length === 0 && !isLoading) {
    return (
      <SectionCard heading="Find a New Company" spacing={4}>
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          No competitor data available for comparison.
        </Alert>
      </SectionCard>
    );
  }

  return (
    <SectionCard heading="Find a New Company" spacing={6}>
      <DemandLeverageSignals
        signals={leverageSignals}
        currentCompanyId={currentCompanyId}
        salaryFormatter={salaryFormatter}
        isLoading={signalsLoading}
        error={signalsError}
        roleName={leverageRoleName}
      />

      <HiddenGemsCard
        gems={hiddenGems}
        isLoading={isLoading}
        metrics={tableMetrics}
        healthData={tableHealthData}
        userSalary={userSalary}
        userRoleLevel={userRoleLevel}
      />

      <CompetitorTable
        metrics={tableMetrics}
        sentiments={tableSentiments}
        healthData={tableHealthData}
        competitors={competitors}
        currentCompanyId={currentCompanyId}
        historicalCompanyIds={highlightedCompanyIds}
        isLoading={isLoading}
        workHistoryCompanies={workHistoryCompanies}
        selectedCompanyRcid={competitorSourceRcid}
        onCompanySelect={onCompanySelect}
        isIndustryFallback={isIndustryFallback}
        userSalary={userSalary}
        userRoleLevel={userRoleLevel}
      />
    </SectionCard>
  );
};
